import { useState, useRef } from 'react';
import { useTheme } from '@/context/ThemeContext';
import { useFizbo, type FizboListing, type KanbanStage } from '@/hooks/useFizbo';
import { kanbanStages } from '@/mocks/fizboData';
import FizboDetailModal from '@/pages/fizbo/components/FizboDetailModal';
import HedefTakibi from '@/pages/fizbo-kanban/components/HedefTakibi';

const stageColors: Record<KanbanStage, { bg: string; text: string; dot: string; count: string }> = {
  Yeni: { bg: 'bg-gray-100 dark:bg-gray-800/50', text: 'text-gray-600 dark:text-gray-400', dot: 'bg-gray-400', count: 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400' },
  İletişim: { bg: 'bg-amber-50 dark:bg-amber-900/20', text: 'text-amber-600 dark:text-amber-400', dot: 'bg-amber-400', count: 'bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400' },
  Görüşme: { bg: 'bg-sky-50 dark:bg-sky-900/20', text: 'text-sky-600 dark:text-sky-400', dot: 'bg-sky-400', count: 'bg-sky-100 dark:bg-sky-900/40 text-sky-600 dark:text-sky-400' },
  Teklif: { bg: 'bg-violet-50 dark:bg-violet-900/20', text: 'text-violet-600 dark:text-violet-400', dot: 'bg-violet-400', count: 'bg-violet-100 dark:bg-violet-900/40 text-violet-600 dark:text-violet-400' },
  Kapandı: { bg: 'bg-green-50 dark:bg-green-900/20', text: 'text-green-600 dark:text-green-400', dot: 'bg-green-500', count: 'bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-400' },
};

const statusColors: Record<string, string> = {
  Kabul: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  Düşünecek: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  Red: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  'Başka Emlakçıda': 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
};

function KanbanCard({
  listing,
  onDragStart,
  onClick,
}: {
  listing: FizboListing;
  onDragStart: (e: React.DragEvent, id: string) => void;
  onClick: () => void;
}) {
  const { isDark } = useTheme();

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, listing.id)}
      onClick={onClick}
      className={`rounded-xl p-3.5 border cursor-grab active:cursor-grabbing transition-all hover:scale-[1.01] select-none ${
        isDark
          ? 'bg-dark-card border-dark-border hover:border-primary-500/40'
          : 'bg-white border-light-border hover:border-primary-500/40'
      }`}
    >
      {/* Top Row */}
      <div className="flex items-start justify-between gap-2 mb-2.5">
        <div className="min-w-0">
          <p className={`text-sm font-semibold truncate ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
            {listing.ownerName}
          </p>
          <p className={`text-xs font-mono mt-0.5 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
            {listing.listingNo}
          </p>
        </div>
        {listing.sellabilityScore && (
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${
            listing.sellabilityScore >= 80
              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
              : listing.sellabilityScore >= 60
              ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
              : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
          }`}>
            {listing.sellabilityScore}
          </span>
        )}
      </div>

      {/* Location */}
      <div className={`flex items-center gap-1.5 mb-2.5 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
        <div className="w-3.5 h-3.5 flex items-center justify-center flex-shrink-0">
          <i className="ri-map-pin-line text-xs" />
        </div>
        <span className="text-xs truncate">{listing.location}</span>
      </div>

      {/* Tags Row */}
      <div className="flex items-center gap-1.5 flex-wrap mb-2.5">
        <span className={`text-xs px-2 py-0.5 rounded-full ${isDark ? 'bg-dark-bg text-dark-muted' : 'bg-light-bg text-light-muted'}`}>
          {listing.propertyType}
        </span>
        <span className={`text-xs px-2 py-0.5 rounded-full ${isDark ? 'bg-dark-bg text-dark-muted' : 'bg-light-bg text-light-muted'}`}>
          {listing.transactionType}
        </span>
        <span className={`text-xs px-2 py-0.5 rounded-full ${statusColors[listing.status]}`}>
          {listing.status}
        </span>
      </div>

      {/* Bottom Row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          <div className="w-3.5 h-3.5 flex items-center justify-center">
            <i className={`ri-time-line text-xs ${listing.daysOld > 30 ? 'text-red-400' : listing.daysOld > 14 ? 'text-yellow-400' : 'text-green-400'}`} />
          </div>
          <span className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>{listing.daysOld} gün</span>
        </div>
        {listing.price && (
          <span className={`text-xs font-semibold ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
            {listing.price >= 1000000
              ? `${(listing.price / 1000000).toFixed(1)}M ₺`
              : `${listing.price.toLocaleString('tr-TR')} ₺`}
          </span>
        )}
      </div>

      {/* Fatigue bar */}
      <div className={`mt-2.5 w-full h-1 rounded-full ${isDark ? 'bg-dark-border' : 'bg-light-border'}`}>
        <div
          className={`h-full rounded-full ${
            listing.fatigueScore < 30 ? 'bg-green-500' :
            listing.fatigueScore < 60 ? 'bg-yellow-500' : 'bg-red-500'
          }`}
          style={{ width: `${listing.fatigueScore}%` }}
        />
      </div>
    </div>
  );
}

export default function FizboKanbanPage() {
  const { isDark } = useTheme();
  const { listings, isLoading, updateListing } = useFizbo();
  const [selectedListing, setSelectedListing] = useState<FizboListing | null>(null);
  const [dragOverStage, setDragOverStage] = useState<KanbanStage | null>(null);
  const dragId = useRef<string | null>(null);

  const handleDragStart = (e: React.DragEvent, id: string) => {
    dragId.current = id;
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, stage: KanbanStage) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverStage(stage);
  };

  const handleDrop = async (e: React.DragEvent, stage: KanbanStage) => {
    e.preventDefault();
    if (!dragId.current) return;
    const id = dragId.current;
    await updateListing(id, { kanbanStage: stage });
    dragId.current = null;
    setDragOverStage(null);
  };

  const handleDragLeave = () => {
    setDragOverStage(null);
  };

  const getStageListings = (stage: KanbanStage) =>
    listings.filter((l) => l.kanbanStage === stage);

  return (
    <div className="flex flex-col h-full space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-primary-500">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-kanban-view text-white text-lg" />
            </div>
          </div>
          <div>
            <h1 className={`text-lg font-semibold ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
              FİZBO Kanban
            </h1>
            <p className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
              {listings.length} ilan · Sürükle &amp; bırak ile aşama değiştir
            </p>
          </div>
        </div>
      </div>

      {/* Hedef Takibi Widget */}
      <HedefTakibi listings={listings} />

      {/* Kanban Board */}
      <div className="flex gap-3 overflow-x-auto pb-4 flex-1">
        {kanbanStages.map((stage) => {
          const stageItems = getStageListings(stage);
          const colors = stageColors[stage];
          const isDragOver = dragOverStage === stage;

          return (
            <div
              key={stage}
              className="flex-shrink-0 w-64 flex flex-col"
              onDragOver={(e) => handleDragOver(e, stage)}
              onDrop={(e) => handleDrop(e, stage)}
              onDragLeave={handleDragLeave}
            >
              {/* Column Header */}
              <div className={`flex items-center justify-between px-3 py-2.5 rounded-xl mb-2 ${colors.bg}`}>
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${colors.dot}`} />
                  <span className={`text-xs font-semibold ${colors.text}`}>{stage}</span>
                </div>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${colors.count}`}>
                  {stageItems.length}
                </span>
              </div>

              {/* Drop Zone */}
              <div
                className={`flex-1 rounded-xl min-h-[200px] transition-all duration-200 space-y-2 p-2 ${
                  isDragOver
                    ? isDark
                      ? 'bg-primary-500/10 border-2 border-dashed border-primary-500/50'
                      : 'bg-primary-50 border-2 border-dashed border-primary-300'
                    : isDark
                    ? 'bg-dark-bg/50 border-2 border-dashed border-transparent'
                    : 'bg-light-bg/50 border-2 border-dashed border-transparent'
                }`}
              >
                {stageItems.map((listing) => (
                  <KanbanCard
                    key={listing.id}
                    listing={listing}
                    onDragStart={handleDragStart}
                    onClick={() => setSelectedListing(listing)}
                  />
                ))}
                {stageItems.length === 0 && (
                  <div className={`flex flex-col items-center justify-center h-24 rounded-lg ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
                    <div className="w-6 h-6 flex items-center justify-center mb-1">
                      <i className="ri-drag-drop-line text-lg opacity-40" />
                    </div>
                    <p className="text-xs opacity-40">Buraya sürükle</p>
                  </div>
                )}
              </div>

              {/* Stage Total */}
              {stageItems.length > 0 && (
                <div className={`mt-2 px-2 py-1.5 rounded-lg text-center ${isDark ? 'bg-dark-card' : 'bg-white'}`}>
                  <p className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
                    {(stageItems.reduce((s, l) => s + (l.price ?? 0), 0) / 1000000).toFixed(1)}M ₺
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {selectedListing && (
        <FizboDetailModal listing={selectedListing} onClose={() => setSelectedListing(null)} />
      )}
    </div>
  );
}
